import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import { createPresignedDownloadHook } from "../presignedDownload";
import {
  SPORTSBOOK_ANALYTICS_FILTER_KEYS,
  SPORTSBOOK_ANALYTICS_PARAMS_SERIALIZER,
  buildSportsbookAnalyticsFilterParams,
  type SportsbookAnalyticsHttpParams,
  type SportsbookAnalyticsFilterKey,
  type SportsbookAnalyticsFilterMetaDto,
} from "./sportsbookAnalyticsFilterParams";
import { filterToken } from "./sportsbookQueryUtils";

export const PROFITABILITY_TOP_LIMIT = 50;
export const PROFITABILITY_DETAILS_QUERY_KEY =
  "SPORTSBOOK_PROFITABILITY" as const;

export type ProfitabilityMetricScope = "total" | "live" | "prematch";

export type ProfitabilityMetricBlock = {
  accepted_bets: number;
  accepted_turnover: number;
  avg_stake: number;
  pnl: number;
  margin: number;
};

type ProfitabilityBlocks = Record<
  ProfitabilityMetricScope,
  ProfitabilityMetricBlock
>;

export type ProfitabilityTopSportDto = ProfitabilityBlocks & {
  sport: string;
};

export type ProfitabilityTopEventDto = ProfitabilityBlocks & {
  event_id: string;
  event_name: string;
  sport: string;
  league: string;
};

export type ProfitabilityFilters = Partial<
  Record<SportsbookAnalyticsFilterKey, string[]>
>;

export type ProfitabilityDetailsParams = ProfitabilityFilters & {
  date: string;
  limit?: number;
};

export type ProfitabilityDetailsResult<Row> = {
  rows: Row[];
  filters: SportsbookAnalyticsFilterMetaDto[];
};

type ProfitabilityResponse<Row> = {
  data?: Row[];
  meta?: { filters?: SportsbookAnalyticsFilterMetaDto[] };
};

export type ProfitabilityMetricsRow = {
  key: string;
  acceptedBets: number;
  acceptedTurnover: number;
  avgStake: number;
  pl: number;
  margin: number;
};

export type ProfitabilitySportRow = ProfitabilityMetricsRow & {
  sport: string;
};

export type ProfitabilityEventRow = ProfitabilityMetricsRow & {
  event: string;
  sport: string;
  league: string;
};

export function buildProfitabilityDetailsParams(
  args: ProfitabilityDetailsParams,
): SportsbookAnalyticsHttpParams {
  return {
    date: args.date,
    limit: args.limit ?? PROFITABILITY_TOP_LIMIT,
    ...buildSportsbookAnalyticsFilterParams(args),
  };
}

export function parseProfitabilityResponse<Row>(
  body: unknown,
): ProfitabilityDetailsResult<Row> {
  const response = body as ProfitabilityResponse<Row> | undefined;
  return {
    rows: Array.isArray(response?.data) ? response.data : [],
    filters: Array.isArray(response?.meta?.filters)
      ? response.meta.filters
      : [],
  };
}

export function mapMetricBlock(
  block: ProfitabilityMetricBlock,
): Omit<ProfitabilityMetricsRow, "key"> {
  return {
    acceptedBets: block.accepted_bets,
    acceptedTurnover: block.accepted_turnover,
    avgStake: block.avg_stake,
    pl: block.pnl,
    margin: block.margin,
  };
}

export function mapTopSportsRows(
  rows: ProfitabilityTopSportDto[],
  scope: ProfitabilityMetricScope,
): ProfitabilitySportRow[] {
  return rows.map((row) => ({
    key: row.sport,
    sport: row.sport,
    ...mapMetricBlock(row[scope]),
  }));
}

export function mapTopEventsRows(
  rows: ProfitabilityTopEventDto[],
  scope: ProfitabilityMetricScope,
): ProfitabilityEventRow[] {
  return rows.map((row) => ({
    key: row.event_id,
    event: row.event_name,
    sport: row.sport,
    league: row.league,
    ...mapMetricBlock(row[scope]),
  }));
}

async function fetchProfitabilityDetails<Row>(
  route: string,
  args: ProfitabilityDetailsParams,
): Promise<ProfitabilityDetailsResult<Row>> {
  const response = await createPrivateAdminPanelInstance(getAccessToken()).get(
    route,
    {
      params: buildProfitabilityDetailsParams(args),
      paramsSerializer: SPORTSBOOK_ANALYTICS_PARAMS_SERIALIZER,
    },
  );
  return parseProfitabilityResponse<Row>(response?.data);
}

function queryKey(
  kind: string,
  params: ProfitabilityDetailsParams,
): unknown[] {
  return [
    PROFITABILITY_DETAILS_QUERY_KEY,
    kind,
    params.date,
    params.limit ?? PROFITABILITY_TOP_LIMIT,
    ...SPORTSBOOK_ANALYTICS_FILTER_KEYS.map((key) =>
      filterToken(params[key]),
    ),
  ];
}

export function useProfitabilityTopSports(
  params: ProfitabilityDetailsParams,
) {
  return useQuery({
    queryKey: queryKey("top-sports", params),
    queryFn: () =>
      fetchProfitabilityDetails<ProfitabilityTopSportDto>(
        API_ROUTES.GET_SPORTSBOOK_PROFITABILITY_TOP_SPORTS,
        params,
      ),
    enabled: Boolean(params.date),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useProfitabilityTopEvents(
  params: ProfitabilityDetailsParams,
) {
  return useQuery({
    queryKey: queryKey("top-events", params),
    queryFn: () =>
      fetchProfitabilityDetails<ProfitabilityTopEventDto>(
        API_ROUTES.GET_SPORTSBOOK_PROFITABILITY_TOP_EVENTS,
        params,
      ),
    enabled: Boolean(params.date),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export const useProfitabilityTopSportsExport =
  createPresignedDownloadHook<Record<string, unknown>>(
    API_ROUTES.GET_SPORTSBOOK_PROFITABILITY_TOP_SPORTS_EXPORT,
    "SPORTSBOOK_PROFITABILITY_TOP_SPORTS_EXPORT",
    { paramsSerializer: SPORTSBOOK_ANALYTICS_PARAMS_SERIALIZER },
  );

export const useProfitabilityTopEventsExport =
  createPresignedDownloadHook<Record<string, unknown>>(
    API_ROUTES.GET_SPORTSBOOK_PROFITABILITY_TOP_EVENTS_EXPORT,
    "SPORTSBOOK_PROFITABILITY_TOP_EVENTS_EXPORT",
    { paramsSerializer: SPORTSBOOK_ANALYTICS_PARAMS_SERIALIZER },
  );
